/**
 * Which shop this till belongs to.
 *
 * One server keeps several shops, each with its own database, told apart at
 * sign-in by an account code. The till remembers the shop it signed in to and
 * keeps everything about it - the session, the receipt counter, the offline
 * copy of the catalogue, the sales waiting to upload - under that shop's own
 * name, so two shops used on one browser can never mix.
 *
 * The shop that was here before accounts has an EMPTY storage key: its tills
 * keep the names they always used, and with them the sales already waiting in
 * their outbox.
 *
 * Read ONCE when the page loads. Changing shop saves the new one and reloads,
 * so nothing on screen can still be holding the previous shop's data.
 */

const KEY = 'pos.account';

export interface ShopRef {
  code: string;
  /** '' for the shop that was here before accounts. */
  storageKey: string;
  name?: string;
}

function read(): ShopRef | null {
  try {
    const r = JSON.parse(localStorage.getItem(KEY) ?? 'null') as ShopRef | null;
    return r && typeof r.code === 'string' && typeof r.storageKey === 'string' ? r : null;
  } catch {
    return null;
  }
}

const loaded = read();

/** The shop this page was loaded for, or null on a till that has never said. */
export function thisShop(): ShopRef | null {
  return loaded;
}

/** Remember the shop for the next load. Does not change the current page. */
export function rememberShop(s: ShopRef): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}

/** A local-storage key belonging to one shop. */
export function keyIn(storageKey: string, key: string): string {
  return storageKey ? `${key}@${storageKey}` : key;
}

/** A local-storage key belonging to the shop this page was loaded for. */
export function shopKey(key: string): string {
  return keyIn(loaded?.storageKey ?? '', key);
}

/** The IndexedDB database holding one shop's offline copy and outbox. */
export function localDbName(storageKey = loaded?.storageKey ?? ''): string {
  return storageKey ? `pos-${storageKey}` : 'bethlehem-pos';
}

export function normalizeCode(s: string): string {
  return s.trim().toLowerCase();
}
